import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import {
  CreditCard,
  CheckCircle,
  Crown,
  Rocket,
  Users,
  ArrowRight,
  Loader2,
  Receipt,
  XCircle,
  Clock
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLAN_ICONS = {
  free: Users,
  standard: Crown,
  pro: Rocket
};

const PLAN_COLORS = {
  free: 'border-gray-200',
  standard: 'border-primary ring-2 ring-primary/20',
  pro: 'border-amber-300 ring-2 ring-amber-200/30'
};

export default function SubscriptionPage() {
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [currentSub, setCurrentSub] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [pollingStatus, setPollingStatus] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [plansRes, subRes, txnRes] = await Promise.all([
        axios.get(`${API}/subscription/plans`),
        axios.get(`${API}/subscription/current`),
        axios.get(`${API}/subscription/transactions`)
      ]);
      setPlans(plansRes.data);
      setCurrentSub(subRes.data);
      setTransactions(txnRes.data);
    } catch (error) {
      console.error('データ取得に失敗', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Handle return from Stripe checkout
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      setPollingStatus('checking');
      pollPaymentStatus(sessionId, 0);
    } else {
      fetchData();
    }
  }, [searchParams, fetchData]);

  const pollPaymentStatus = async (sessionId, attempts) => {
    const maxAttempts = 8;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setPollingStatus('timeout');
      fetchData();
      return;
    }

    try {
      const res = await axios.get(`${API}/subscription/checkout/status/${sessionId}`);
      
      if (res.data.payment_status === 'paid') {
        setPollingStatus('success');
        fetchData();
        return;
      } else if (res.data.status === 'expired') {
        setPollingStatus('expired');
        fetchData();
        return;
      }

      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('ステータス確認エラー', error);
      setPollingStatus('error');
      fetchData();
    }
  };

  const handleSelectPlan = async (planId) => {
    if (planId === 'free') return;
    
    setProcessing(planId);
    try {
      const originUrl = window.location.origin;
      const res = await axios.post(`${API}/subscription/checkout`, {
        plan_id: planId,
        origin_url: originUrl
      });
      
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (error) {
      console.error('チェックアウト作成に失敗', error);
      alert('決済処理の開始に失敗しました: ' + (error.response?.data?.detail || error.message));
    } finally {
      setProcessing(null);
    }
  };

  const handleCancel = async () => {
    try {
      await axios.post(`${API}/subscription/cancel`);
      fetchData();
    } catch (error) {
      console.error('キャンセルに失敗', error);
      alert('キャンセルに失敗しました: ' + (error.response?.data?.detail || error.message));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('ja-JP');
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('ja-JP').format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in max-w-5xl" data-testid="subscription-page">
      {/* Payment status notification */}
      {pollingStatus && (
        <Card className={`card-surface ${pollingStatus === 'success' ? 'border-emerald-300 bg-emerald-50' : pollingStatus === 'checking' ? 'border-amber-300 bg-amber-50' : 'border-red-300 bg-red-50'}`}>
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              {pollingStatus === 'checking' && (
                <>
                  <Loader2 size={24} className="animate-spin text-amber-600" />
                  <div>
                    <p className="font-medium text-amber-800">決済ステータスを確認中...</p>
                    <p className="text-sm text-amber-600">しばらくお待ちください</p>
                  </div>
                </>
              )}
              {pollingStatus === 'success' && (
                <>
                  <CheckCircle size={24} weight="fill" className="text-emerald-600" />
                  <div>
                    <p className="font-medium text-emerald-800">決済が完了しました</p>
                    <p className="text-sm text-emerald-600">プランがアップグレードされました</p>
                  </div>
                </>
              )}
              {(pollingStatus === 'timeout' || pollingStatus === 'error' || pollingStatus === 'expired') && (
                <>
                  <XCircle size={24} weight="fill" className="text-red-600" />
                  <div>
                    <p className="font-medium text-red-800">決済の確認に失敗しました</p>
                    <p className="text-sm text-red-600">時間をおいて再度お試しください</p>
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <div>
        <h1 className="text-3xl font-bold tracking-tight text-foreground">
          プラン・課金管理
        </h1>
        <p className="text-muted-foreground mt-1">
          サブスクリプションプランの管理
        </p>
      </div>

      {/* Current subscription */}
      {currentSub && (
        <Card className="card-surface" data-testid="current-subscription">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard size={20} className="text-primary" />
              現在のプラン
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-bold">{currentSub.plan_name}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${currentSub.status === 'active' ? 'bg-emerald-100 text-emerald-800 border-emerald-200' : 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                    {currentSub.status === 'active' ? '有効' : 'キャンセル済'}
                  </span>
                </div>
                <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                  <span>利用者: {currentSub.current_clients} / {currentSub.max_clients === 9999 ? '無制限' : currentSub.max_clients}名</span>
                  {currentSub.price > 0 && <span>月額: ¥{formatAmount(currentSub.price)}</span>}
                  {currentSub.next_billing_date && <span>次回請求: {formatDate(currentSub.next_billing_date)}</span>}
                </div>
              </div>
              {currentSub.plan_id !== 'free' && currentSub.status === 'active' && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" size="sm" className="text-destructive hover:text-destructive" data-testid="cancel-subscription-button">
                      キャンセル
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>サブスクリプションをキャンセルしますか？</AlertDialogTitle>
                      <AlertDialogDescription>フリープランに戻ります。利用者数が5名を超えている場合、一部機能が制限されます。</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>戻る</AlertDialogCancel>
                      <AlertDialogAction onClick={handleCancel}>キャンセルする</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
            {/* Client usage bar */}
            <div className="mt-4">
              <div className="flex justify-between text-xs text-muted-foreground mb-1">
                <span>利用者数</span>
                <span>{currentSub.current_clients} / {currentSub.max_clients === 9999 ? '∞' : currentSub.max_clients}</span>
              </div>
              <div className="h-2 bg-secondary rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    currentSub.max_clients === 9999 ? 'bg-primary' :
                    currentSub.current_clients / currentSub.max_clients > 0.8 ? 'bg-red-500' :
                    currentSub.current_clients / currentSub.max_clients > 0.5 ? 'bg-amber-500' : 'bg-emerald-500'
                  }`}
                  style={{ width: `${currentSub.max_clients === 9999 ? 10 : Math.min(100, (currentSub.current_clients / currentSub.max_clients) * 100)}%` }}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Plans */}
      <div>
        <h2 className="text-lg font-semibold mb-4">プラン選択</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {plans.map(plan => {
            const Icon = PLAN_ICONS[plan.id] || Users;
            const isCurrentPlan = currentSub?.plan_id === plan.id;
            
            return (
              <Card
                key={plan.id}
                className={`card-surface relative transition-all hover:shadow-lg ${PLAN_COLORS[plan.id]} ${isCurrentPlan ? 'ring-2 ring-primary' : ''}`}
                data-testid={`plan-card-${plan.id}`}
              >
                {plan.id === 'standard' && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-primary text-primary-foreground text-xs font-medium px-3 py-1 rounded-full">
                      おすすめ
                    </span>
                  </div>
                )}
                <CardHeader className="text-center pb-2">
                  <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-2">
                    <Icon size={24} className="text-primary" weight="duotone" />
                  </div>
                  <CardTitle>{plan.name}</CardTitle>
                  <div className="mt-2">
                    {plan.price > 0 ? (
                      <div>
                        <span className="text-3xl font-bold">¥{formatAmount(plan.price)}</span>
                        <span className="text-muted-foreground text-sm">/月</span>
                      </div>
                    ) : (
                      <span className="text-3xl font-bold">無料</span>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-center text-sm text-muted-foreground">
                    利用者 {plan.max_clients === 9999 ? '無制限' : `${plan.max_clients}名まで`}
                  </div>
                  <ul className="space-y-2">
                    {plan.features.map((feat, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm">
                        <CheckCircle size={16} className="text-emerald-500 shrink-0" weight="fill" />
                        {feat}
                      </li>
                    ))}
                  </ul>
                  <div className="pt-2">
                    {isCurrentPlan ? (
                      <Button className="w-full" variant="outline" disabled data-testid={`plan-button-${plan.id}`}>
                        現在のプラン
                      </Button>
                    ) : plan.price > 0 ? (
                      <Button
                        className="w-full btn-active"
                        onClick={() => handleSelectPlan(plan.id)}
                        disabled={processing === plan.id}
                        data-testid={`plan-button-${plan.id}`}
                      >
                        {processing === plan.id ? (
                          <>
                            <Loader2 size={16} className="mr-2 animate-spin" />
                            処理中...
                          </>
                        ) : (
                          <>
                            {currentSub?.plan_id !== 'free' ? 'プラン変更' : 'アップグレード'}
                            <ArrowRight size={16} className="ml-2" />
                          </>
                        )}
                      </Button>
                    ) : (
                      <Button className="w-full" variant="outline" disabled data-testid={`plan-button-${plan.id}`}>
                        無料プラン
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Transaction history */}
      {transactions.length > 0 && (
        <Card className="card-surface" data-testid="transaction-history">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Receipt size={20} className="text-primary" />
              決済履歴
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {transactions.map((txn, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-secondary/50">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${txn.payment_status === 'paid' ? 'bg-emerald-100' : 'bg-gray-100'}`}>
                      {txn.payment_status === 'paid' ? (
                        <CheckCircle size={16} className="text-emerald-600" weight="fill" />
                      ) : (
                        <Clock size={16} className="text-gray-500" />
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-medium">
                        {SUBSCRIPTION_PLANS_LABELS[txn.plan_id] || txn.plan_id}プラン
                      </p>
                      <p className="text-xs text-muted-foreground">{formatDate(txn.created_at)}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium">¥{formatAmount(txn.amount)}</p>
                    <p className={`text-xs ${txn.payment_status === 'paid' ? 'text-emerald-600' : 'text-muted-foreground'}`}>
                      {txn.payment_status === 'paid' ? '支払済' : txn.payment_status === 'pending' ? '保留中' : txn.payment_status}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

const SUBSCRIPTION_PLANS_LABELS = {
  free: 'フリー',
  standard: 'スタンダード',
  pro: 'プロ'
};
